import type { TaskAttachment } from '../types/crm';
import { formatSupabaseError } from './formatSupabaseError';
import { scopedStoragePath } from './portal';
import { getDbSupabase, supabase } from './supabase';

export const TASK_ATTACHMENTS_BUCKET = 'crm-task-attachments';
export const TASK_ATTACHMENT_MAX_BYTES = 20 * 1024 * 1024;

const ATTACH_SELECT =
  'id,task_id,file_name,content_type,byte_size,storage_path,uploaded_by,created_at';

function db() {
  return getDbSupabase();
}

export function formatTaskAttachmentBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export async function listTaskAttachments(taskId: string): Promise<TaskAttachment[]> {
  const { data, error } = await db()
    .from('task_attachments')
    .select(ATTACH_SELECT)
    .eq('task_id', taskId)
    .order('created_at', { ascending: false });
  if (error) throw new Error(formatSupabaseError(error, 'attachment'));
  return (data ?? []) as TaskAttachment[];
}

export async function uploadTaskAttachment(input: {
  taskId: string;
  file: File;
  uploadedBy?: string;
}): Promise<TaskAttachment> {
  if (input.file.size > TASK_ATTACHMENT_MAX_BYTES) {
    throw new Error(
      `“${input.file.name}” is larger than ${formatTaskAttachmentBytes(TASK_ATTACHMENT_MAX_BYTES)}.`,
    );
  }

  const safeName = input.file.name.replace(/[^\w.\-()+ ]+/g, '_').trim() || 'file';
  const path = scopedStoragePath(
    `task_attachments/${input.taskId}/${crypto.randomUUID()}-${safeName}`,
  );

  const { error: upErr } = await supabase.storage.from(TASK_ATTACHMENTS_BUCKET).upload(path, input.file, {
    cacheControl: '3600',
    upsert: false,
    contentType: input.file.type || 'application/octet-stream',
  });
  if (upErr) throw new Error(formatSupabaseError(upErr, 'attachment'));

  const row = {
    task_id: input.taskId,
    file_name: input.file.name,
    content_type: input.file.type || 'application/octet-stream',
    byte_size: input.file.size,
    storage_path: path,
    uploaded_by: input.uploadedBy?.trim() || null,
  };
  const { data, error } = await db()
    .from('task_attachments')
    .insert(row)
    .select(ATTACH_SELECT)
    .single();
  if (error) {
    await supabase.storage.from(TASK_ATTACHMENTS_BUCKET).remove([path]).catch(() => undefined);
    throw new Error(formatSupabaseError(error, 'attachment'));
  }
  return data as TaskAttachment;
}

export async function uploadPendingTaskAttachments(input: {
  taskId: string;
  files: File[];
  uploadedBy?: string;
}): Promise<void> {
  for (const file of input.files) {
    await uploadTaskAttachment({
      taskId: input.taskId,
      file,
      uploadedBy: input.uploadedBy,
    });
  }
}

export async function getTaskAttachmentSignedUrl(storagePath: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from(TASK_ATTACHMENTS_BUCKET)
    .createSignedUrl(storagePath, 60 * 30);
  if (error) throw new Error(formatSupabaseError(error, 'attachment'));
  if (!data?.signedUrl) throw new Error('Could not create download link.');
  return data.signedUrl;
}

export async function deleteTaskAttachment(attachment: TaskAttachment): Promise<void> {
  const { error } = await db().from('task_attachments').delete().eq('id', attachment.id);
  if (error) throw new Error(formatSupabaseError(error, 'attachment'));
  await supabase.storage
    .from(TASK_ATTACHMENTS_BUCKET)
    .remove([attachment.storage_path])
    .catch(() => undefined);
}
